import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getPushStatus,
  enablePush,
  disablePush,
  sendTestPush,
  isIOS,
} from "../../../utils/pushNotifications";

const STATUS_TEXT = {
  enabled: { label: "On", className: "bg-green-100 text-green-700" },
  disabled: { label: "Off", className: "bg-gray-100 text-gray-600" },
  denied: { label: "Blocked", className: "bg-red-100 text-red-700" },
  "needs-install": { label: "Add to Home Screen", className: "bg-amber-100 text-amber-700" },
  unsupported: { label: "Not supported", className: "bg-gray-100 text-gray-600" },
};

const buttonClass =
  "px-4 py-2.5 rounded-xl text-sm font-semibold active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed";

const NotificationsSection = () => {
  const [status, setStatus] = useState(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    getPushStatus().then(setStatus).catch(() => setStatus("unsupported"));
  }, []);

  // enablePush() must run first in the tap handler so Safari shows the prompt.
  const handleEnable = () => {
    setIsBusy(true);
    enablePush()
      .then((next) => {
        setStatus(next);
        if (next === "enabled") toast.success("Notifications enabled on this device");
        else if (next === "denied") toast.error("Notifications were blocked");
      })
      .catch((err) => toast.error(err.message || "Could not enable notifications"))
      .finally(() => setIsBusy(false));
  };

  const handleDisable = async () => {
    setIsBusy(true);
    try {
      setStatus(await disablePush());
      toast.info("Notifications turned off on this device");
    } catch (err) {
      toast.error("Could not turn off notifications");
    } finally {
      setIsBusy(false);
    }
  };

  const handleTest = async () => {
    setIsBusy(true);
    try {
      await sendTestPush();
      toast.success("Test sent - it should appear in a few seconds");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsBusy(false);
    }
  };

  const badge = status && STATUS_TEXT[status];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-4">
        <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center text-white flex-shrink-0">
          <span className="text-xs font-semibold">4</span>
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-gray-900">Order Notifications</h2>
          <p className="text-xs text-gray-500">
            Get a notification on this device when a new order arrives, even when the admin is not open.
          </p>
        </div>
        {badge && (
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${badge.className}`}>
            {badge.label}
          </span>
        )}
      </div>

      {status === "needs-install" && (
        <div className="text-sm text-gray-700 space-y-1">
          <p>On iPad/iPhone, notifications only work from the Home Screen app:</p>
          <ol className="list-decimal pl-5 text-gray-600">
            <li>Tap the Share button in Safari</li>
            <li>Choose <b>Add to Home Screen</b></li>
            <li>Open the admin from the new icon and come back here</li>
          </ol>
          <p className="text-xs text-gray-500">Requires iPadOS / iOS 16.4 or later.</p>
        </div>
      )}

      {status === "unsupported" && (
        <p className="text-sm text-gray-600">
          This browser does not support notifications.
          {isIOS() && " Update the iPad to iPadOS 16.4 or later."}
        </p>
      )}

      {status === "denied" && (
        <p className="text-sm text-gray-600">
          Notifications are blocked for this site.{" "}
          {isIOS()
            ? "Open iPad Settings → Notifications → this app, and allow notifications."
            : "Allow them from the lock icon next to the address bar, then reload."}
        </p>
      )}

      {(status === "disabled" || status === "enabled") && (
        <div className="flex flex-wrap gap-3">
          {status === "disabled" ? (
            <button
              type="button"
              onClick={handleEnable}
              disabled={isBusy}
              className={`${buttonClass} bg-blue-600 hover:bg-blue-700 text-white`}
            >
              🔔 Enable notifications
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={handleTest}
                disabled={isBusy}
                className={`${buttonClass} bg-blue-600 hover:bg-blue-700 text-white`}
              >
                Send test notification
              </button>
              <button
                type="button"
                onClick={handleDisable}
                disabled={isBusy}
                className={`${buttonClass} bg-gray-100 hover:bg-gray-200 text-gray-800`}
              >
                Turn off
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsSection;
